import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { LayoutOptions } from '../types'

interface LayoutState extends LayoutOptions {
  setPageSize: (size: 'a4' | 'letter') => void
  setCardsPerPage: (count: number) => void
  setIncludeBleed: (include: boolean) => void
  setIncludeCutLines: (include: boolean) => void
  setFrontsOnly: (frontsOnly: boolean) => void
  setCutLineColor: (color: string) => void
  setCutLineStyle: (style: 'ticks' | 'full') => void
  setCutLineWidth: (width: number) => void
  setCutLineOverCards: (over: boolean) => void
  setMaxPdfMb: (mb: number | null) => void
  reset: () => void
}

const defaultOptions: LayoutOptions = {
  pageSize: 'a4',
  cardsPerPage: 9,
  includeBleed: true,
  includeCutLines: true,
  frontsOnly: false,
  cutLineColor: '#000000',
  cutLineStyle: 'ticks',
  cutLineWidth: 1.0,
  cutLineOverCards: false,
  maxPdfMb: null,
}

// C8: persist the layout options to localStorage so the user's choices
// (page size, bleed, cut lines, TCG, etc.) survive a reload — same pattern
// as listsStore. Only the option fields are persisted, not the actions.
export const useLayoutStore = create<LayoutState>()(
  persist(
    (set) => ({
      ...defaultOptions,
      setPageSize: (pageSize) => set({ pageSize }),
      setCardsPerPage: (cardsPerPage) => set({ cardsPerPage }),
      setIncludeBleed: (includeBleed) => set({ includeBleed }),
      setIncludeCutLines: (includeCutLines) => set({ includeCutLines }),
      setFrontsOnly: (frontsOnly) => set({ frontsOnly }),
      setCutLineColor: (cutLineColor) => set({ cutLineColor }),
      setCutLineStyle: (cutLineStyle) => set({ cutLineStyle }),
      setCutLineWidth: (cutLineWidth) => set({ cutLineWidth }),
      setCutLineOverCards: (cutLineOverCards) => set({ cutLineOverCards }),
      setMaxPdfMb: (maxPdfMb) => set({ maxPdfMb }),
      reset: () => set(defaultOptions),
    }),
    {
      name: 'mpcfill-layout', // localStorage key
      version: 1,
      // Persist only the option values; the setter functions are recreated
      // on every load and must not be serialised.
      partialize: (state) => ({
        pageSize: state.pageSize,
        cardsPerPage: state.cardsPerPage,
        includeBleed: state.includeBleed,
        includeCutLines: state.includeCutLines,
        frontsOnly: state.frontsOnly,
        cutLineColor: state.cutLineColor,
        cutLineStyle: state.cutLineStyle,
        cutLineWidth: state.cutLineWidth,
        cutLineOverCards: state.cutLineOverCards,
        maxPdfMb: state.maxPdfMb,
      }),
    }
  )
)
